import React, { useState } from "react";
import {
  Col,
  Input,
  Row,
  Form,
  DatePicker,
  Select,
  TimePicker,
  Button,
  message,
  Spin,
} from "antd";
import { useForm } from "antd/es/form/Form";
import dayjs from "dayjs";
import CommonFunctions from "../../../Shared/CommonFunctions";
import enrollmentServices from "../../../../Services/enrollmentServices";
import AppointmentService from "../../../../Services/AppointmentService";

function IPDPatientInvite({ inviteDetails, handleInviteOk, handleCancel }) {
  const [form] = useForm();
  const [endTime, setEndTime] = useState(dayjs().add(15, "minutes"));
  const [save, setSave] = useState(false);
  const [consReason, setConsReason] = useState("");
  const [consCode, setConsCode] = useState("");
  const [consultationReasonList, setConsultationReasonList] = useState([]);
  const [callType, setCallType] = useState("APPT_VIDEO");

  const storedLoginResult =
    sessionStorage.getItem("login_result") ||
    localStorage.getItem("login_result");
  const loginResultObject = storedLoginResult
    ? JSON.parse(storedLoginResult)
    : null;

  const fetchConsReasons = async (filterPayload) => {
    const payload = {
      chiefComplaint: "",
      status: 1,
    };
    let finalPayload = payload;
    if (filterPayload) {
      finalPayload = {
        ...payload,
        ...filterPayload,
      };
    }
    try {
      const response = await AppointmentService.AllConsReasons(finalPayload);
      setConsultationReasonList(response?.data || []);
    } catch (e) {
      console.error("Error fetching consultation reasons:", e);
    }
  };

  const handleConsReasonChange = (e) => {
    const value = e.target.value;
    setConsReason(value);
    if (value && value.length >= 3) {
      fetchConsReasons({ chiefComplaint: value });
    } else {
      setConsultationReasonList([]);
    }
  };

  const handleConsSelectChange = (reason) => {
    const chief = reason?.chiefComplaint || "";
    setConsReason(chief);
    setConsCode(reason?.complaintCode || "");
    form.setFieldsValue({ consultationReason: chief });
    setConsultationReasonList([]);
  };

  const handleCallTypeChange = (value) => {
    setCallType(value);
    let fee = 0;
    if (value === "APPT_INPERSON") {
      fee = loginResultObject?.user?.inpersonFee ?? 0;
    } else if (value === "APPT_VIDEO") {
      fee = loginResultObject?.user?.videoFee ?? 0;
    } else if (value === "APPT_AUDIO") {
      fee = loginResultObject?.user?.audioFee ?? 50;
    } else if (value === "APPT_HOMEVISIT") {
      fee = loginResultObject?.user?.homeVisitFee ?? 0;
    }
    form.setFieldsValue({ consultationFee: fee });
  };

  const handleStartTimeChange = (time) => {
    if (time) {
      const newEndTime = dayjs(time).add(15, "minutes");
      setEndTime(newEndTime);
      form.setFieldsValue({ endTime: newEndTime });
    } else {
      setEndTime(null);
    }
  };

  const handleClose = () => {
    form.resetFields();
    if (handleCancel) {
      handleCancel();
    } else if (handleInviteOk) {
      handleInviteOk();
    }
  };

  const disabledDate = (current) => {
    return current && current < dayjs().startOf("day");
  };

  const handleAmountRestriction = (e) => {
    const { key, target } = e;
    const value = target.value;

    if (
      key === "Backspace" ||
      key === "Delete" ||
      key === "ArrowLeft" ||
      key === "ArrowRight" ||
      key === "Tab"
    ) {
      return;
    }

    if (!/[0-9.]/.test(key)) {
      e.preventDefault();
      return;
    }

    if (key === "." && value.includes(".")) {
      e.preventDefault();
      return;
    }

    const [intPart, decimalPart] = value.split(".");

    if (!value.includes(".") && intPart.length >= 6) {
      e.preventDefault();
      return;
    }

    if (value.includes(".") && decimalPart?.length >= 2) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (payload) => {
    setSave(true);
    try {
      const currentUserId = loginResultObject?.user?.userId || "";
      const nowStr = CommonFunctions.convertUTCDate(
        new Date(),
        "YYYY-MM-DD HH:mm:ss"
      );

      const fee = Number(payload.consultationFee) || 0;

      const data = {
        consultationReason: consReason || payload.consultationReason || "",
        complaintCode: consCode || "",
        createdBy: currentUserId,
        createdDate: nowStr,
        email: inviteDetails?.email || "",
        account_Id: loginResultObject?.user?.account_Id || "",
        clinic_Id: loginResultObject?.user?.clinic_Id || "",
        firstName: inviteDetails?.firstName || "",
        lastName: inviteDetails?.lastName || "",
        specialityCode: loginResultObject?.user?.specialityCode || "",
        mobile: inviteDetails?.mobile || "",
        modifiedBy: currentUserId,
        modifiedDate: nowStr,
        patientId: inviteDetails?.patientId || "",
        // Cash payment only as requested
        paymentConsultation: "BC",
        paymentType: "Cash",
        paymentReceived: "Y",
        status: 1,
        appointmentDate: CommonFunctions.convertDate(payload.appointmentDate),
        startTime: dayjs(payload.startTime).local().format("hh:mm A"),
        endTime: dayjs(payload.endTime).local().format("hh:mm A"),
        serviceName:
          payload.serviceCode === "APPT_VIDEO"
            ? "Video Consultation"
            : payload.serviceCode === "APPT_AUDIO"
            ? "Audio Consultation"
            : payload.serviceCode === "APPT_INPERSON"
            ? "Inperson Consultation"
            : payload.serviceCode === "APPT_HOMEVISIT"
            ? "Home Visit Consultation"
            : "",
        serviceCode: payload.serviceCode,
        consultationFee: fee,
        totalAmount: fee,
        userId: currentUserId,
        patientType: "E",
        patientRegFeeYN: "N",
      };

      const result = await enrollmentServices.Invite(data);

      if (result?.data?.code === "FAILED") {
        message.error(result?.data?.message || "Scheduling Consultation Failed, Retry");
        setSave(false);
        return;
      }

      message.success("Consultation Scheduled Successfully");
      form.resetFields();
      if (handleInviteOk) {
        handleInviteOk();
      }
    } catch (error) {
      console.error("Error scheduling consultation:", error);
      message.error("An error occurred, please try again");
    } finally {
      setSave(false);
    }
  };

  const patientFullName =
    [inviteDetails?.firstName, inviteDetails?.lastName]
      .filter(Boolean)
      .join(" ") || "-";

  return (
    <div className="relative">
      {save && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <Spin size="large" />
        </div>
      )}

      {/* Patient details banner */}
      <div className="bg-[#f7fafc] border border-[#e2e8f0] rounded-lg p-3 mb-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex gap-2">
            <span className="font-semibold text-[#189FAA]">Patient Name:</span>
            <span className="text-[#111827] font-medium">{patientFullName}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold text-[#189FAA]">Patient ID:</span>
            <span className="text-[#111827] font-medium">{inviteDetails?.patientId || "-"}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold text-[#189FAA]">Email:</span>
            <span className="text-[#4b5563]">{inviteDetails?.email || "N/A"}</span>
          </div>
          <div className="flex gap-2">
            <span className="font-semibold text-[#189FAA]">Mobile:</span>
            <span className="text-[#4b5563]">{inviteDetails?.mobile || "N/A"}</span>
          </div>
        </div>
      </div>

      <Form
        className="formDesign"
        name="ipdConsultationInvite"
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          serviceCode: "APPT_VIDEO",
          consultationFee: loginResultObject?.user?.videoFee || "500",
          appointmentDate: dayjs(),
          startTime: dayjs(),
          endTime: dayjs().add(15, "minutes"),
        }}
      >
        <div className="rounded-lg bg-white p-4 border border-[#edf1f7] shadow-sm">
          <Row gutter={16}>
            {/* Appointment Date */}
            <Col span={12}>
              <div className="relative mt-2">
                <span className="absolute -top-2 z-10 left-3 bg-white px-1 text-xs text-gray-600 font-medium">
                  Appointment Date <span className="text-red-500">*</span>
                </span>
                <Form.Item
                  name="appointmentDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select Appointment Date!",
                    },
                  ]}
                >
                  <DatePicker
                    format={"DD/MM/YYYY"}
                    className="w-full mt-1 rounded-lg px-4 py-2"
                    disabledDate={disabledDate}
                  />
                </Form.Item>
              </div>
            </Col>

            {/* Service Type */}
            <Col span={12}>
              <div className="relative mt-2">
                <span className="absolute -top-2 z-10 left-3 bg-white px-1 text-xs text-gray-600 font-medium">
                  Service Type <span className="text-red-500">*</span>
                </span>
                <Form.Item
                  name="serviceCode"
                  rules={[
                    {
                      required: true,
                      message: "Please select Service Type!",
                    },
                  ]}
                >
                  <Select
                    className="w-full mt-1"
                    placeholder="Select Consultation Type"
                    onChange={handleCallTypeChange}
                  >
                    <Select.Option value="APPT_VIDEO">Video</Select.Option>
                    <Select.Option value="APPT_AUDIO">Audio</Select.Option>
                    <Select.Option value="APPT_INPERSON">In-person</Select.Option>
                    <Select.Option value="APPT_HOMEVISIT">Home Visit</Select.Option>
                  </Select>
                </Form.Item>
              </div>
            </Col>

            {/* Start Time */}
            <Col span={12}>
              <div className="relative mt-2">
                <span className="absolute -top-2 z-10 left-3 bg-white px-1 text-xs text-gray-600 font-medium">
                  Start Time <span className="text-red-500">*</span>
                </span>
                <Form.Item
                  name="startTime"
                  rules={[
                    {
                      required: true,
                      message: "Please select Start Time!",
                    },
                  ]}
                >
                  <TimePicker
                    use12Hours
                    format="h:mm a"
                    className="w-full mt-1 rounded-lg px-4 py-2"
                    onChange={handleStartTimeChange}
                    minuteStep={5}
                    needConfirm={false}
                    allowClear={false}
                    inputReadOnly={true}
                  />
                </Form.Item>
              </div>
            </Col>

            {/* End Time */}
            <Col span={12}>
              <div className="relative mt-2">
                <span className="absolute -top-2 z-10 left-3 bg-white px-1 text-xs text-gray-600 font-medium">
                  End Time <span className="text-red-500">*</span>
                </span>
                <Form.Item
                  name="endTime"
                  rules={[
                    {
                      required: true,
                      message: "Please select End Time!",
                    },
                  ]}
                >
                  <TimePicker
                    id="endTime"
                    use12Hours
                    format="h:mm a"
                    className="w-full mt-1 rounded-lg px-4 py-2"
                    value={endTime}
                    minuteStep={5}
                    needConfirm={false}
                    allowClear={false}
                    inputReadOnly={true}
                  />
                </Form.Item>
              </div>
            </Col>

            {/* Reason for Consultation */}
            <Col span={12}>
              <div className="relative mt-2">
                <span className="absolute -top-2 z-10 left-3 bg-white px-1 text-xs text-gray-600 font-medium">
                  Reason for Consultation
                </span>
                <Form.Item name="consultationReason">
                  <div className="relative">
                    <Input
                      placeholder="Enter chief complaint / reason"
                      value={consReason}
                      onChange={handleConsReasonChange}
                      className="rounded-lg mt-1"
                    />
                    {consultationReasonList.length > 0 && (
                      <ul className="absolute top-11 left-0 w-full mt-1 bg-white border border-[#edf1f7] rounded-md shadow-lg max-h-36 overflow-y-auto z-30">
                        {consultationReasonList.map((reason, index) => (
                          <li
                            key={index}
                            onClick={() => handleConsSelectChange(reason)}
                            className="px-3 py-2 text-[13px] font-sans hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            {reason.chiefComplaint}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Form.Item>
              </div>
            </Col>

            {/* Fees */}
            <Col span={12}>
              <div className="relative mt-2">
                <span className="absolute -top-2 z-10 left-3 bg-white px-1 text-xs text-gray-600 font-medium">
                  {`Consultation Fee (${loginResultObject?.user?.currencyCode || "INR"})`}
                </span>
                <Form.Item
                  name="consultationFee"
                  rules={[
                    {
                      required: true,
                      message: "Please enter consultation fee!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Enter fee amount"
                    onKeyDown={handleAmountRestriction}
                    className="rounded-lg mt-1"
                  />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end mt-6">
          <button
            type="button"
            className="border border-[#d1d5db] rounded-lg bg-white px-5 py-2 text-sm font-semibold text-[#374151] hover:bg-gray-50 transition-colors"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={save}
            className="bg-[#008f9f] hover:bg-[#007a8a] text-white rounded-lg px-6 py-2 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {callType === "APPT_VIDEO" ? "Schedule & Send Invite" : "Schedule Consultation"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default IPDPatientInvite;
